import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, UserPlus, X, ShieldCheck, ShieldOff } from "lucide-react";

const ROLE_BADGE = {
  admin: "bg-purple-100 text-purple-700",
  clinician: "bg-blue-100 text-blue-700",
  lab: "bg-cyan-100 text-cyan-700",
  patient: "bg-slate-100 text-slate-600",
};

export default function AdminUsers() {
  const [users, setUsers] = useState(null);
  const [showAdd, setShowAdd] = useState(false);

  const load = async () => setUsers((await api.get("/admin/users")).data.users);
  useEffect(() => { load(); }, []);

  if (!users) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-brand" /></div>;

  const toggle = async (u) => {
    try { await api.patch(`/admin/users/${u.id}`, { active: !u.active }); await load(); toast.success(u.active ? "Disabled" : "Enabled"); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div>
      <div className="flex items-center justify-between">
        <div>
          <div className="eyebrow">Administration</div>
          <h1 className="font-head text-3xl font-bold text-ink mt-1">Users</h1>
        </div>
        <button onClick={() => setShowAdd(true)} data-testid="add-user-btn"
          className="inline-flex items-center gap-2 rounded-lg bg-brand text-white px-4 py-2.5 font-semibold hover:bg-brand-hover transition-colors">
          <UserPlus className="h-4 w-4" /> Add staff user
        </button>
      </div>

      <div className="mt-6 rounded-xl border border-slate-200 bg-white overflow-x-auto">
        <table className="w-full text-sm min-w-[720px]">
          <thead className="bg-slate-50 border-b border-slate-200 text-left text-ink-muted">
            <tr>
              <th className="px-4 py-3 font-semibold">Name</th>
              <th className="px-4 py-3 font-semibold">Email</th>
              <th className="px-4 py-3 font-semibold">Role</th>
              <th className="px-4 py-3 font-semibold">MANOGENE ID</th>
              <th className="px-4 py-3 font-semibold">Status</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {users.map((u) => (
              <tr key={u.id} className="hover:bg-slate-50 transition-colors" data-testid={`user-row-${u.id}`}>
                <td className="px-4 py-3 font-semibold text-ink">{u.name}</td>
                <td className="px-4 py-3 text-ink-soft">{u.email}</td>
                <td className="px-4 py-3"><span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${ROLE_BADGE[u.role]}`}>{u.role}</span></td>
                <td className="px-4 py-3 mono text-brand">{u.manogene_id || "—"}</td>
                <td className="px-4 py-3">
                  {u.active ? <span className="text-green-700 text-xs font-semibold">Active</span> : <span className="text-red-600 text-xs font-semibold">Disabled</span>}
                </td>
                <td className="px-4 py-3 text-right">
                  <button onClick={() => toggle(u)} data-testid={`toggle-user-${u.id}`}
                    className="inline-flex items-center gap-1 text-xs font-semibold text-ink-soft hover:text-brand">
                    {u.active ? <><ShieldOff className="h-3.5 w-3.5" /> Disable</> : <><ShieldCheck className="h-3.5 w-3.5" /> Enable</>}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showAdd && <AddUserModal onClose={() => setShowAdd(false)} onSaved={async () => { setShowAdd(false); await load(); }} />}
    </div>
  );
}

function AddUserModal({ onClose, onSaved }) {
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "clinician" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async () => {
    if (!form.name || !form.email || !form.password) return toast.error("All fields required");
    setBusy(true);
    try { await api.post("/admin/users", form); toast.success("User created"); onSaved(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()} data-testid="add-user-modal">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-head text-xl font-semibold text-ink">Add staff user</h3>
          <button onClick={onClose} className="text-ink-muted hover:text-ink"><X className="h-5 w-5" /></button>
        </div>
        {[["Full name", "name", "text"], ["Email", "email", "email"], ["Temporary password", "password", "text"]].map(([label, key, type]) => (
          <label key={key} className="block mb-3">
            <span className="text-sm font-medium text-ink-soft">{label}</span>
            <input type={type} value={form[key]} onChange={set(key)} data-testid={`new-user-${key}`}
              className="mt-1.5 w-full rounded-lg border border-slate-300 px-3 py-2.5 outline-none focus:border-brand" />
          </label>
        ))}
        <label className="block mb-5">
          <span className="text-sm font-medium text-ink-soft">Role</span>
          <select value={form.role} onChange={set("role")} data-testid="new-user-role"
            className="mt-1.5 w-full rounded-lg border border-slate-300 px-3 py-2.5 outline-none focus:border-brand">
            <option value="clinician">Clinician</option>
            <option value="lab">Lab partner</option>
            <option value="admin">Administrator</option>
          </select>
        </label>
        <div className="flex gap-3">
          <button onClick={onClose} className="flex-1 rounded-lg border border-slate-300 py-2.5 font-semibold text-ink-soft hover:bg-slate-50">Cancel</button>
          <button onClick={save} disabled={busy} data-testid="save-user-btn"
            className="flex-1 rounded-lg bg-brand text-white py-2.5 font-semibold hover:bg-brand-hover transition-colors disabled:opacity-50">
            {busy ? "Creating…" : "Create user"}
          </button>
        </div>
      </div>
    </div>
  );
}
